import React, { useState } from 'react';
import { Nav } from '../../components/nav';
import { useAuth } from '../../navigation/AuthProvider';
import { ROLE_PERMISSIONS } from '../../permissions/rolePermissions';
import '../css/RoleInformation.css';

export const RoleInformation = () => {
  const { user } = useAuth();
  const [selectedRole, setSelectedRole] = useState('superadmin');
  
  // Check if user has permission to view this page
  const canViewRoles = user?.role === 'super-admin' || user?.role === 'admin';
  
  // Map permissions to actual page names
  const pageMapping = {
    'schedule': 'Tidsschema',
    'employees': 'Användare',
    'cases:overtime': 'Övertidsärenden',
    'cases:absence': 'Frånvaroärenden',
    'projects': 'Projekt',
    'projects:groups': 'Projektgrupper',
    'projects:overview': 'Projektöversikt',
    'customers': 'Kunder',
    'payroll': 'Lönhantering',
    'payroll:ob': 'OB-översikt',
    'payroll:overtime': 'Övertid-översikt',
    'payroll:absence': 'Frånvaro-översikt',
    'payroll:export': 'Exportera lön',
    'settings:company': 'Bolagsinställningar'
  };

  // Group pages logically
  const pageCategories = [
    {
      name: 'Schemaläggning & Personal',
      pages: ['schedule', 'employees']
    },
    {
      name: 'Ärenden',
      pages: ['cases:overtime', 'cases:absence']
    },
    {
      name: 'Projekt',
      pages: ['projects', 'projects:groups', 'projects:overview']
    },
    {
      name: 'Kunder',
      pages: ['customers']
    },
    {
      name: 'Lönhantering',
      pages: ['payroll', 'payroll:ob', 'payroll:overtime', 'payroll:absence', 'payroll:export']
    },
    {
      name: 'Inställningar',
      pages: ['settings:company']
    }
  ];

  // Role display names and descriptions
    const roleInfo = {
    superadmin: {
        name: "Superadmin",
        description:
        "Har fullständig kontroll över hela TimeSmart-systemet. Kan hantera alla inställningar, all data och alla användare – inklusive administratörer. Ansvarar för systemstruktur, behörigheter och övergripande konfiguration."
    },

    administratör: {
        name: "Administratör",
        description:
        "Har full kontroll över den egna verksamheten. Kan hantera användare, schema, projekt, ärenden och lön inom företaget. Har dock inte åtkomst till systemövergripande inställningar eller andra administratörers behörigheter."
    },

    schemaläggare: {
        name: "Schemaläggare",
        description:
        "Arbetar operativt med planering. Kan skapa och redigera schema, hantera projekt samt behandla övertids- och frånvaroärenden. Saknar åtkomst till lönehantering och systeminställningar."
    },

    lönehanterare: {
        name: "Lönehanterare",
        description:
        "Ansvarar för lönearbete. Kan hantera lön, OB, övertid och export till lönesystem. Har läsbehörighet till övriga delar av systemet men kan inte ändra schema, projekt eller inställningar."
    },

    granskare: {
        name: "Granskare",
        description:
        "Har endast läsbehörighet. Kan se schema, projekt, ärenden och lönedata för uppföljning och kontroll men kan inte skapa, ändra eller godkänna något."
    },

    användare: {
        name: "Användare",
        description:
        "Har endast tillgång till appens användarvy. Kan se sitt eget schema, skicka in övertids- och frånvaroärenden samt följa status på sina egna ärenden. Saknar åtkomst till administration och hantering av andra användare."
    }
    };


  // Permission text
  const permissionText = {
    'edit': 'Redigera',
    'view': 'Läs-behörighet',
    'none': 'Ingen åtkomst'
  };

  if (!canViewRoles) {
    return (
      <div className="role-info-page">
        <Nav />
        <div className="access-denied">
          <h2>Åtkomst nekad</h2>
          <p>Du har inte behörighet att se den här sidan.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="role-info-page">
      <Nav />
      
      <div className="page-header">
        <h1>Rollöversikt</h1>
        <p className="page-subtitle">Vilka sidor har varje roll åtkomst till?</p>
      </div>

      {/* Role selector */}
      <div className="role-selector">
        {Object.keys(ROLE_PERMISSIONS).map(role => (
          <button
            key={role}
            className={`role-button ${selectedRole === role ? 'active' : ''}`}
            onClick={() => setSelectedRole(role)}
          >
            {roleInfo[role].name}
          </button>
        ))}
      </div>

      {/* Role summary with description */}
      <div className="role-summary">
        <div className="role-title">
          <h2>{roleInfo[selectedRole].name}</h2>
          <p className="role-description">{roleInfo[selectedRole].description}</p>
        </div>
        <div className="role-stats">
          <div className="stat">
            <span className="stat-count edit">
              {Object.values(ROLE_PERMISSIONS[selectedRole]).filter(v => v === 'edit').length}
            </span>
            <span className="stat-label">Redigera</span>
          </div>
          <div className="stat">
            <span className="stat-count view">
              {Object.values(ROLE_PERMISSIONS[selectedRole]).filter(v => v === 'view').length}
            </span>
            <span className="stat-label">Läs-behörighet</span>
          </div>
          <div className="stat">
            <span className="stat-count none">
              {Object.values(ROLE_PERMISSIONS[selectedRole]).filter(v => v === 'none').length}
            </span>
            <span className="stat-label">Ingen åtkomst</span>
          </div>
        </div>
      </div>

      {/* Pages table */}
      <div className="pages-table">
        <div className="table-header">
          <div>Sida</div>
          <div>Åtkomst</div>
        </div>
        
        {pageCategories.map(category => (
          <div key={category.name}>
            <div className="category-name">{category.name}</div>
            {category.pages.map(pageKey => {
              const permission = ROLE_PERMISSIONS[selectedRole][pageKey] || 'none';
              return (
                <div key={pageKey} className="page-row">
                  <div className="page-name">{pageMapping[pageKey]}</div>
                  <div className="access-info">
                    <span className={`access-badge ${permission}`}>
                      {permissionText[permission]}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default RoleInformation;